/**
 * ZERO-SKILL ONBOARDING (EXPERT-NETWORKS.md §7, E6) — the Expert Network's declared setup steps as a horizontal
 * stepper: ask (a plain question) · connect (its connection, set up with that connection's own wizard + Test) ·
 * confirm (what was learned) · propose (the first request, pre-filled for the person to send — never auto-run).
 * Plus, for a room that hasn't planned, "start from a template". Domain-agnostic: every word is the network's own.
 */
import React, { useEffect, useState } from 'react';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const KIND_LABEL = { ask: 'Question', connect: 'Connect', confirm: 'Confirm', propose: 'First request' };

export function TemplatePicker({ templates, onApply }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  if (!(templates || []).length) return null;
  return (
    <div className="space-y-1.5" data-testid="expert-templates">
      <p className="text-[11px] text-gray-500">Start this network as an Expert Network from a template — its teams, rules,
        facts and setup steps. You can change everything afterwards.</p>
      {templates.map((t) => (
        <div key={t.id} className="rounded-md border border-gray-200 px-2 py-1.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800">{t.title}</span>
            <button type="button" disabled={!!busy} data-testid={`template-apply-${t.id}`}
              className={`${btn} ml-auto bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
              onClick={async () => {
                setBusy(t.id); setErr('');
                try { await onApply(t.id); } catch (e) { setErr((e && e.message) || "Couldn't apply it."); } finally { setBusy(''); }
              }}>
              {busy === t.id ? 'Starting…' : 'Start from this'}
            </button>
          </div>
          {t.summary ? <p className="mt-0.5 text-[11px] text-gray-600">{t.summary}</p> : null}
        </div>
      ))}
      {err ? <p className="text-[11px] text-rose-600">{err}</p> : null}
    </div>
  );
}

export default function RoomOnboarding({ value, onAct, onOpenConnection, loadConnections }) {
  const v = value || {};
  const steps = v.steps || [];
  const cur = steps.find((s) => s.status === 'current');
  const [answer, setAnswer] = useState('');
  const [conns, setConns] = useState([]);
  const [conn, setConn] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setAnswer(cur?.answer || ''); setErr(''); }, [cur?.index]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (cur?.kind !== 'connect' || !loadConnections) return;
    loadConnections(cur.connector).then((rows) => setConns(rows || [])).catch(() => setConns([]));
  }, [cur?.index, cur?.kind]);                                                       // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (req) => {
    setBusy(true); setErr('');
    try { await onAct(req); } catch (e) { setErr((e && e.message) || "Couldn't do that."); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 text-[12px]" data-testid="room-onboarding">
      <ol className="flex flex-wrap items-center gap-1" aria-label="Setup steps">
        {steps.map((s) => (
          <li key={s.index} data-testid={`onboarding-step-${s.index}`} data-status={s.status}
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${s.status === 'done' ? 'bg-emerald-50 text-emerald-700'
              : s.status === 'current' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
            {s.index + 1}. {KIND_LABEL[s.kind] || s.kind}
          </li>
        ))}
      </ol>
      {v.done ? (
        <div className="flex items-center gap-2" data-testid="onboarding-done">
          <p className="text-[12px] text-emerald-700">Setup is finished.</p>
          {v.canAct ? <button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={() => act({ action: 'restart' })}>Start over</button> : null}
        </div>
      ) : cur ? (
        <div className="space-y-1.5">
          <p className="text-[12px] text-gray-800" data-testid="onboarding-text">{cur.text}</p>
          {!v.canAct ? <p className="text-[11px] text-gray-400">The room&apos;s owner or builders finish this setup.</p> : null}
          {v.canAct && cur.kind === 'ask' ? (
            <div className="flex gap-2">
              <input value={answer} onChange={(e) => setAnswer(e.target.value)} aria-label="Your answer" data-testid="onboarding-answer"
                className="flex-1 rounded border border-gray-300 px-2 py-1 text-[12px]" />
              <button type="button" disabled={busy || !answer.trim()} data-testid="onboarding-next"
                className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
                onClick={() => act({ action: 'answer', value: answer })}>Next</button>
            </div>
          ) : null}
          {v.canAct && cur.kind === 'connect' ? (
            <div className="space-y-1">
              <button type="button" className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}
                data-testid="onboarding-open-connection" onClick={() => onOpenConnection && onOpenConnection(cur.connector)}>
                Set up the connection
              </button>
              <div className="flex gap-2">
                <select value={conn} onChange={(e) => setConn(e.target.value)} aria-label="Your connection"
                  data-testid="onboarding-connection" className="flex-1 rounded border border-gray-300 px-1.5 py-0.5 text-[11px]">
                  <option value="">{conns.length ? 'Choose the connection you set up…' : 'No tested connection yet'}</option>
                  {conns.map((c) => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                </select>
                <button type="button" disabled={busy || !conn} data-testid="onboarding-check-connection"
                  className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
                  onClick={() => act({ action: 'connect', value: conn })}>Check &amp; continue</button>
              </div>
            </div>
          ) : null}
          {v.canAct && cur.kind === 'confirm' ? (
            <div className="space-y-1">
              <ul className="rounded-md bg-gray-50 px-2 py-1 text-[11px] text-gray-700" data-testid="onboarding-summary">
                {Object.entries(v.answers || {}).map(([k, val]) => <li key={k}><span className="font-medium">{k}</span>: {val}</li>)}
                {Object.entries(v.connections || {}).map(([k, val]) => <li key={k}><span className="font-medium">{k}</span>: connected ({val})</li>)}
              </ul>
              <button type="button" disabled={busy} data-testid="onboarding-confirm"
                className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
                onClick={() => act({ action: 'confirm' })}>Looks right</button>
            </div>
          ) : null}
          {v.canAct && cur.kind === 'propose' ? (
            <button type="button" disabled={busy} data-testid="onboarding-propose"
              className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
              onClick={() => act({ action: 'propose' })}>Write it for me to send</button>
          ) : null}
          {v.canAct && cur.index > 0 ? (
            <button type="button" disabled={busy} className="text-[11px] text-gray-500 hover:underline" onClick={() => act({ action: 'back' })}>Back</button>
          ) : null}
        </div>
      ) : null}
      {err ? <p className="text-[11px] text-rose-600" data-testid="onboarding-error">{err}</p> : null}
    </div>
  );
}
